import CloseIcon from '@mui/icons-material/ExitToAppRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded';
import SwapVertIcon from '@mui/icons-material/SwapVertRounded';
import SwitchAccessShortcutRoundedIcon from '@mui/icons-material/SwitchAccessShortcutRounded';
import {
  Button,
} from '@mui/material';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ContextTag from '../../../../../../Common/ContextTag';
import { useGetLiveProjectCommandContextsQuery } from '../../../../../../generated/core-api/graphql-react-query';
import useGlobalStoreContext from '../../../../../Contexts/GlobalContext/Contexts/GlobalStoreContext/useGlobalStoreContext';
import useSessionContext from '../../../../../LiveContext/SessionContextProvider/useSessionContext';
import { useOverflowContext } from '../../../Overflow/OverflowContext';
import ContextSelectorMenu from './ContextSelectorMenu';
import StyledBox, { StyledMenuItem } from './styles';

export function ContextSelectorMenuItem() {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const { projectId: [projectId] } = useGlobalStoreContext();
  const { context, setContext } = useSessionContext();

  const contexts = useGetLiveProjectCommandContextsQuery({ input: { id: projectId ?? '' } }, { enabled: !!projectId });

  const currentContext = React.useMemo(() => {
    if (!context) return null;
    return contexts.data?.getProject.commandContexts.find((c) => c.textLower === context) ?? null;
  }, [context, contexts.data?.getProject.commandContexts]);
  const { t } = useTranslation();

  if (!contexts.data?.getProject.commandContexts.length) {
    return null;
  }

  return (
    <StyledMenuItem
      disableRipple={!!context}
      disableTouchRipple={!!context}
      onClick={(e) => {
        if (!context) {
          setAnchorEl(e.currentTarget);
        }
      }}
      className={clsx({ selected: !!context })}
    >
      {currentContext ? (
        <div className="secondary">
          <ContextTag context={currentContext} />
          <Button
            title={t('home-page.footer-toolbar.context.change')}
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(e.currentTarget);
            }}
          >
            <SwapVertIcon />
          </Button>
          <Button
            title={t('home-page.footer-toolbar.context.exit')}
            onClick={() => { setContext(null); }}
          >
            <CloseIcon />
          </Button>
        </div>
      ) : (
        <div>
          <SwitchAccessShortcutRoundedIcon />
          {t(`home-page.footer-toolbar.context.${context ? 'change' : 'select'}`)}
        </div>
      )}
      <ContextSelectorMenu anchorEl={anchorEl} setAnchorEl={setAnchorEl} contexts={contexts.data.getProject.commandContexts} />
    </StyledMenuItem>
  );
}

export default function ContextSelector() {
  const { projectId: [projectId] } = useGlobalStoreContext();
  const { context, setContext } = useSessionContext();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { t } = useTranslation();

  const contexts = useGetLiveProjectCommandContextsQuery({ input: { id: projectId ?? '' } }, { enabled: !!projectId });
  const { registerItemMenuComponent } = useOverflowContext();

  React.useEffect(() => {
    registerItemMenuComponent('context', <ContextSelectorMenuItem key="context" />);
  }, []);

  if (!contexts.data?.getProject.commandContexts.length) {
    return null;
  }

  return (
    <StyledBox data-testid="context-selector" className={clsx({ active: !!context, selecting: !!anchorEl })}>
      <Button
        onClick={(e) => { setAnchorEl(e.currentTarget); }}
        className={clsx('selectButton')}
        disableRipple
        disableFocusRipple
        disableTouchRipple
        title={t(`home-page.footer-toolbar.context.${context ? 'change' : 'select'}`)}
      >
        <ExpandMoreIcon />
      </Button>
      <Button
        onClick={(e) => {
          if (!context) {
            setAnchorEl(e.currentTarget);
          } else {
            setContext(null);
          }
        }}
        disableRipple
        disableFocusRipple
        disableTouchRipple
        data-testid="context-selector-exit"
        className={clsx('mainButton', { active: !!context })}
        title={t(`home-page.footer-toolbar.context.${context ? 'exit' : 'select'}`)}
      >
        <SwitchAccessShortcutRoundedIcon />
      </Button>
      <ContextSelectorMenu anchorEl={anchorEl} setAnchorEl={setAnchorEl} contexts={contexts.data.getProject.commandContexts} />
    </StyledBox>
  );
}
